import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Input,
    Button,
    VStack,
    HStack,
    FormControl,
    FormLabel,
    CheckboxGroup,
    Checkbox,
    Text,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Badge,
    RadioGroup,
    Radio,
} from "@chakra-ui/react";
import api from "../../utils/axios";

const CreateUserModal = ({ isOpen = false, onClose = () => {}, onCreate = () => {}, locationId }) => {
    const [formData, setFormData] = useState({ name: "", lastName: "", email: "", password: "", phoneNumber: "", featureIds: [], role: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [features, setFeatures] = useState([]);
    const [featuresLoading, setFeaturesLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setFormData({ name: "", lastName: "", email: "", password: "", phoneNumber: "", featureIds: [], role: 0 });
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !locationId) return;

        const fetchFeatures = async () => {
            setFeaturesLoading(true);
            try {
                const response = await api.get(`features/location/${locationId}`);
                setFeatures(response.data || []);
            } catch (error) {
                setFeatures([]);
            } finally {
                setFeaturesLoading(false);
            }
        };

        fetchFeatures();
    }, [isOpen, locationId]);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onCreate(formData);
            onClose();
        } catch (error) {
            console.error("Error creating user:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Dodajte novog korisnika</ModalHeader>
                <ModalCloseButton />
                <ModalBody px={0}>
                    <Tabs colorScheme="brand" px={6}>
                        <TabList>
                            <Tab>Podaci</Tab>
                            <Tab>
                                Značajke
                                {formData.featureIds.length > 0 && (
                                    <Badge ml={2} colorScheme="brand" borderRadius="full">
                                        {formData.featureIds.length}
                                    </Badge>
                                )}
                            </Tab>
                            <Tab>Uloga</Tab>
                        </TabList>
                        <TabPanels>
                            <TabPanel px={0}>
                                <VStack spacing={4} align="stretch">
                                    <FormControl>
                                        <FormLabel>Ime korisnika</FormLabel>
                                        <Input
                                            placeholder="Unesite ime"
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Prezime korisnika</FormLabel>
                                        <Input
                                            placeholder="Unesite prezime"
                                            value={formData.lastName}
                                            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Email korisnika</FormLabel>
                                        <Input
                                            type="email"
                                            placeholder="Unesite email"
                                            value={formData.email}
                                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Lozinka korisnika</FormLabel>
                                        <Input
                                            type="password"
                                            placeholder="Unesite lozinku"
                                            value={formData.password}
                                            onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel>Telefonski broj korisnika</FormLabel>
                                        <Input
                                            type="tel"
                                            placeholder="Unesite telefonski broj"
                                            value={formData.phoneNumber}
                                            onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                        />
                                    </FormControl>
                                </VStack>
                            </TabPanel>

                            <TabPanel px={0}>
                                {featuresLoading ? (
                                    <Text fontSize="sm" color="gray.500">Učitavanje značajki...</Text>
                                ) : features.length > 0 ? (
                                    <>
                                        <Text fontSize="sm" color="gray.500" mb={3}>
                                            Odaberite barem jednu značajku.
                                        </Text>
                                        <CheckboxGroup
                                            value={formData.featureIds}
                                            onChange={(featureIds) => setFormData(prev => ({ ...prev, featureIds }))}
                                        >
                                            <VStack align="start" spacing={3}>
                                                {features.map((feature) => (
                                                    <Checkbox key={feature.id} value={feature.id}>
                                                        {feature.name}
                                                    </Checkbox>
                                                ))}
                                            </VStack>
                                        </CheckboxGroup>
                                    </>
                                ) : (
                                    <Text fontSize="sm" color="gray.500">Nema dostupnih značajki za ovu lokaciju.</Text>
                                )}
                            </TabPanel>

                            <TabPanel px={0}>
                                <RadioGroup
                                    value={String(formData.role)}
                                    onChange={(value) => setFormData(prev => ({ ...prev, role: parseInt(value, 10) }))}
                                >
                                    <VStack align="start" spacing={3}>
                                        <Radio value="0">Korisnik</Radio>
                                        <Radio value="1">Administrator</Radio>
                                    </VStack>
                                </RadioGroup>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing={4}>
                        <Button
                            colorScheme="blue"
                            onClick={handleSubmit}
                            isDisabled={
                                isSubmitting ||
                                !formData.name ||
                                !formData.email ||
                                !formData.password ||
                                formData.featureIds.length === 0
                            }
                        >
                            {isSubmitting ? "Dodavanje..." : "Dodaj"}
                        </Button>
                        <Button onClick={onClose}>Povratak</Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CreateUserModal;
